import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Select, message } from 'antd';
import moment from 'moment';

const { Option } = Select;

const SubscriptionModal = ({
  open,
  onCancel,
  onSubmit,
  selectedSubscription,
  subscriptionForm,
  isEditSubscription,
}) => {
  const { t } = useTranslation();
  const handleOk = async () => {
    try {
      await subscriptionForm.validateFields();
      const values = subscriptionForm.getFieldsValue();

      const body = {
        ...(isEditSubscription
          ? {
              endTime: moment(values.endTime).toISOString(),
            }
          : {
              startTime: moment(values.startTime).toISOString(),
              endTime: values.endTime
                ? moment(values.endTime).toISOString()
                : undefined,
              paymentType: values.paymentType,
            }),
      };

      onSubmit(body, selectedSubscription);
      subscriptionForm.resetFields();
    } catch (error) {
      message.error(t('subscriptions.fillFieldsError'));
    }
  };

  return (
    <Modal
      title={
        selectedSubscription
          ? isEditSubscription
            ? t('subscriptions.editSubscription')
            : t('subscriptions.changeSubscription')
          : t('subscriptions.addSubscription')
      }
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={t('common.ok')}
      cancelText={t('common.cancel')}
    >
      <Form
        form={subscriptionForm}
        layout="vertical"
        initialValues={{
          paymentType: 'prepaid',
        }}
      >
        {!isEditSubscription && (
          <>
            <Form.Item
              name="paymentType"
              label={t('subscriptions.paymentType')}
              rules={[
                {
                  required: true,
                  message: t('subscriptions.selectPaymentType'),
                },
              ]}
            >
              <Select>
                <Option value="prepaid">{t('subscriptions.prepaid')}</Option>
                <Option value="postpaid">{t('subscriptions.postpaid')}</Option>
                <Option value="monthly">{t('subscriptions.monthly')}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={t('subscriptions.startTime')}
              name="startTime"
              rules={[
                { required: true, message: t('subscriptions.selectStartTime') },
              ]}
            >
              <Input type="datetime-local" />
            </Form.Item>
          </>
        )}

        <Form.Item
          label={t('subscriptions.endTime')}
          name="endTime"
          dependencies={['startTime', 'paymentType']}
          rules={
            isEditSubscription
              ? [
                  {
                    required: true,
                    message: t('subscriptions.endTimeRequired'),
                  },
                ]
              : [
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const paymentType = getFieldValue('paymentType');
                      const startTime = getFieldValue('startTime');

                      if (paymentType !== 'monthly') {
                        return Promise.resolve();
                      }

                      if (!value) {
                        return Promise.reject(
                          new Error(t('subscriptions.endTimeRequiredMonthly'))
                        );
                      }

                      if (!startTime) {
                        return Promise.reject(
                          new Error(
                            t('subscriptions.startTimeRequiredBeforeEnd')
                          )
                        );
                      }

                      const start = moment(startTime);
                      const end = moment(value);
                      const months = end.diff(start, 'months', true);

                      if (months < 1 || months > 24) {
                        return Promise.reject(
                          new Error(t('subscriptions.durationBetween'))
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]
          }
        >
          <Input type="datetime-local" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SubscriptionModal;
